import { useState, useCallback } from 'react'
import { Head } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import PageHeader from '@/components/ui/PageHeader'
import Notification from '@/components/ui/Notification'
import DataTable, { useFlash, usePagination, type DataTableColumn } from '@/components/ui/DataTable'
import { FormSelect } from '@/components/ui/form'
import Pagination from '@/components/ui/Pagination'
import { Badge } from '@/components/catalyst/badge'
import { TrashIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline'

// ─── Types ──────────────────────────────────────

interface PurchaseOrderRow {
  id: number
  po_number: string | null
  order_id: number | null
  sales_rep: string | null
  brand_name: string | null
  customer_name: string | null
  po_date: string | null
  arrival_date: string | null
  state: string
}

interface PurchaseOrdersIndexProps {
  purchase_orders: PurchaseOrderRow[]
  states: [string, string][]
}

// ─── Helpers ────────────────────────────────────

function formatDate(d: string | null): string {
  if (!d) return '—'
  const [y, m, day] = d.split('-')
  return `${m}/${day}/${y}`
}

const STATE_COLORS: Record<string, string> = {
  request_invoice: 'yellow',
  check_the_invoice: 'sky',
  pay_deposit: 'orange',
  deposit_paid: 'lime',
  pay_the_balance: 'amber',
  pay_full_amount: 'emerald',
}

const PAGE_SIZES = [30, 50, 100, 200]

// ─── Component ──────────────────────────────────

export default function PurchaseOrdersIndex({ purchase_orders: initialOrders, states }: PurchaseOrdersIndexProps) {
  const [orders, setOrders] = useState(initialOrders)
  const [notifMessage, setNotifMessage] = useState('')
  const [showNotif, setShowNotif] = useState(false)
  const { flashRow, flash } = useFlash()

  const csrfToken = typeof document !== 'undefined'
    ? document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
    : ''

  const notify = useCallback((msg: string) => { setNotifMessage(msg); setShowNotif(true) }, [])

  // ─── State update ───────────────────────────
  const updateState = useCallback(async (row: PurchaseOrderRow, newState: string) => {
    try {
      const res = await fetch(`/purchase_orders/${row.id}.json`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ purchase_order: { state: newState } }),
      })
      if (res.ok) {
        setOrders(prev => prev.map(po => po.id === row.id ? { ...po, state: newState } : po))
        flash(row.id, 'success')
        notify('Saved:State updated')
      } else { flash(row.id, 'error') }
    } catch { flash(row.id, 'error') }
  }, [csrfToken, flash, notify])

  // ─── Delete ─────────────────────────────────
  const deleteOrder = useCallback(async (id: number) => {
    if (!confirm('Are you sure you want to delete this purchase order?')) return
    try {
      const res = await fetch(`/purchase_orders/${id}.json`, { method: 'DELETE', headers: { 'X-CSRF-Token': csrfToken } })
      if (res.ok) { setOrders(prev => prev.filter(po => po.id !== id)); notify('Deleted:Purchase order removed') }
    } catch { /* silent */ }
  }, [csrfToken, notify])

  // ─── Options ────────────────────────────────
  const stateOptions = states.map(([val, label]) => ({ value: val, label }))
  const pageSizeOptions = PAGE_SIZES.map(n => ({ value: String(n), label: `${n} per page` }))

  // ─── Pagination ─────────────────────────────
  const { pageSize, setPageSize, currentPage, setCurrentPage, totalPages, paginatedRows, totalCount } = usePagination(orders)

  // ─── Columns ────────────────────────────────
  const columns: DataTableColumn<PurchaseOrderRow>[] = [
    {
      key: 'po_number', label: 'PO Number',
      render: row => (
        <a href={`/purchase_orders/${row.id}/export_purchase_order`} target="_blank" rel="noopener noreferrer" className="font-medium text-indigo-600 hover:underline dark:text-indigo-400">
          {row.po_number || `#${row.id}`}
        </a>
      ),
      sortValue: row => row.po_number || '',
      filterValue: row => row.po_number || '',
    },
    {
      key: 'sales_rep', label: 'Sales Rep',
      render: row => <>{row.sales_rep || '—'}</>,
      sortValue: row => row.sales_rep || '',
      filterValue: row => row.sales_rep || '',
    },
    {
      key: 'brand_name', label: 'Brand',
      render: row => <>{row.brand_name || '—'}</>,
      sortValue: row => row.brand_name || '',
      filterValue: row => row.brand_name || '',
    },
    {
      key: 'po_date', label: 'PO Date',
      render: row => <>{formatDate(row.po_date)}</>,
      sortValue: row => row.po_date || '',
      filterValue: row => row.po_date || '',
    },
    {
      key: 'arrival_date', label: 'Arrival Date',
      render: row => <>{formatDate(row.arrival_date)}</>,
      sortValue: row => row.arrival_date || '',
    },
    {
      key: 'state', label: 'State',
      render: row => (
        <Badge color={(STATE_COLORS[row.state] || 'zinc') as 'zinc'}>
          {(row.state || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || '—'}
        </Badge>
      ),
      sortValue: row => row.state || '',
      filterValue: row => row.state || '',
      editSelect: {
        options: stateOptions,
        getValue: row => row.state || '',
        onSelect: (row, val) => updateState(row, val),
      },
    },
  ]

  // ─── Render ─────────────────────────────────
  return (
    <>
      <Head title="Purchase Orders" />
      <Notification message={notifMessage} show={showNotif} onClose={() => setShowNotif(false)} />

      <PageHeader title="Purchase Orders" icon={<DocumentDuplicateIcon className="h-5 w-5" />} actions={
        <div className="w-36">
          <FormSelect value={String(pageSize)} onChange={v => { setPageSize(Number(v)); setCurrentPage(0) }} options={pageSizeOptions} />
        </div>
      } />

      <div className="mb-2">
        <Badge color="zinc"><span className="text-xs font-medium">{totalCount} purchase orders</span></Badge>
      </div>

      <DataTable
        rows={paginatedRows}
        columns={columns}
        rowKey={row => row.id}
        flashRowId={flashRow?.id ?? null}
        flashStatus={flashRow?.status ?? null}
        emptyText="No purchase orders"
        filteredEmptyText="No purchase orders match your filters"
        actions={row => (
          <button onClick={() => deleteOrder(row.id)} className="rounded p-1 text-zinc-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-500/10 dark:hover:text-red-400" title="Delete">
            <TrashIcon className="h-4 w-4" />
          </button>
        )}
      />

      <Pagination currentPage={currentPage} totalPages={totalPages} pageSize={pageSize} totalCount={totalCount} onPageChange={setCurrentPage} />
    </>
  )
}

PurchaseOrdersIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
